import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import * as C from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

function SelectField({ label, value, onChange, options, optional, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label} {optional && <span className="text-kid-muted font-normal text-xs">optional</span>}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-12 rounded-xl bg-white" data-testid={testid}><SelectValue placeholder="Choose one…" /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ChipMulti({ label, values, onToggle, options, optional }) {
  return (
    <div className="space-y-2">
      <Label>{label} {optional && <span className="text-kid-muted font-normal text-xs">optional · choose any</span>}</Label>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => {
          const active = values.includes(o.value);
          return (
            <button type="button" key={o.value} onClick={() => onToggle(o.value)}
              className={`px-4 py-2 rounded-full text-sm font-poppins border transition-all ${
                active ? "bg-kid-teal text-white border-kid-teal" : "bg-white text-kid-ink border-kid-line hover:border-kid-teal"}`}>
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const STEPS = ["Family", "Child", "Support", "Funding", "School", "Priority"];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [family, setFamily] = useState({ caregiver_first_name: "", province: "", city_region: "", preferred_language: "en", primary_reason: "" });
  const [child, setChild] = useState({
    nickname: "", age_range: "", diagnosis_status: "", assessment_status: "", school_status: "",
    main_support_needs: [], current_services: [], funding_status: "", dtc_status: "", cdb_status: "", oap_status: "", school_support_concerns: [],
  });
  const [answers, setAnswers] = useState({ urgent_priority: "", documents_available: [], knows_about_dtc: false, knows_about_oap: false });

  useEffect(() => {
    // preload existing data if resuming
    (async () => {
      try {
        const [f, ch, intake] = await Promise.all([
          api.get("/family").then((r) => r.data).catch(() => null),
          api.get("/child-profile").then((r) => r.data).catch(() => null),
          api.get("/intake/current").then((r) => r.data).catch(() => null),
        ]);
        if (f) setFamily((s) => ({ ...s, ...f }));
        if (ch) setChild((s) => ({ ...s, ...ch }));
        if (intake?.answers) setAnswers((s) => ({ ...s, ...intake.answers }));
      } catch (_) {}
    })();
  }, []);

  const toggle = (obj, setObj, key, val) =>
    setObj({ ...obj, [key]: obj[key].includes(val) ? obj[key].filter((x) => x !== val) : [...obj[key], val] });

  const validStep = () => {
    if (step === 0) return family.caregiver_first_name && family.province && family.primary_reason;
    if (step === 1) return child.nickname && child.age_range && child.diagnosis_status;
    if (step === 5) return answers.urgent_priority;
    return true;
  };

  const persist = async () => {
    if (step === 0) await api.post("/family", family);
    if (step === 1 || step === 2 || step === 3 || step === 4) await api.post("/child-profile", child);
    await api.post("/intake", { answers, current_step: STEPS[step] });
  };

  const next = async () => {
    setError("");
    if (!validStep()) { setError("Please complete the required fields to continue."); return; }
    setBusy(true);
    try {
      await persist();
      setStep((s) => s + 1);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const saveExit = async () => {
    setBusy(true);
    try { await persist(); toast.success("Progress saved. Come back any time."); navigate("/app"); }
    catch (e) { setError(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const finish = async () => {
    if (!validStep()) { setError("Please choose your main priority to continue."); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/family", family);
      await api.post("/child-profile", child);
      await api.post("/intake/complete", { answers });
      await refresh();
      navigate("/app/roadmap?new=1");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const progress = Math.round(((step + 1) / STEPS.length) * 100);

  return (
    <div className="min-h-screen bg-kid-mist py-8 px-5" data-testid="onboarding-page">
      <div className="max-w-xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <img src="/assets/kidpath-mark-light.png" alt="" className="h-9" />
          <span className="font-display text-2xl font-extrabold"><span style={{ color: "var(--logo-navy)" }}>Kid</span><span style={{ color: "var(--logo-teal)" }}>Path</span></span>
        </div>

        <div className="mb-6">
          <div className="flex justify-between text-xs font-poppins text-kid-muted mb-2">
            <span>Step {step + 1} of {STEPS.length} · {STEPS[step]}</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" data-testid="onboarding-progress" />
        </div>

        <div className="kid-card p-7 sm:p-8">
          {error && <div className="text-sm text-kid-coral bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5" data-testid="onboarding-error">{error}</div>}

          {step === 0 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-extrabold text-kid-navy">Let's start with your family</h2>
                <p className="text-kid-muted text-sm mt-1">A calm, plain-language start. A few minutes, no jargon.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Your first name</Label>
                <Input value={family.caregiver_first_name} onChange={(e) => setFamily({ ...family, caregiver_first_name: e.target.value })}
                       className="h-12 rounded-xl bg-white" data-testid="onb-caregiver-name" placeholder="Jamie" />
              </div>
              <SelectField label="What province do you live in?" value={family.province} testid="onb-province"
                           onChange={(v) => setFamily({ ...family, province: v })} options={C.PROVINCES} />
              {family.province && family.province !== "ON" && (
                <div className="text-sm text-kid-muted bg-kid-sun/10 border border-kid-sun/30 rounded-xl px-4 py-3">
                  KidPath is currently built with Ontario-first support guidance. You can still review general federal information, but province-specific roadmap logic may be limited.
                </div>
              )}
              <div className="space-y-1.5">
                <Label>City or region <span className="text-kid-muted font-normal text-xs">optional</span></Label>
                <Input value={family.city_region || ""} onChange={(e) => setFamily({ ...family, city_region: e.target.value })}
                       className="h-12 rounded-xl bg-white" data-testid="onb-city" placeholder="e.g. Toronto" />
              </div>
              <SelectField label="What brings you to KidPath?" value={family.primary_reason} testid="onb-reason"
                           onChange={(v) => setFamily({ ...family, primary_reason: v })} options={C.PRIMARY_REASONS} />
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-extrabold text-kid-navy">About your child</h2>
                <p className="text-kid-muted text-sm mt-1">We only ask what helps us personalize your roadmap.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Child's first name or nickname</Label>
                <Input value={child.nickname} onChange={(e) => setChild({ ...child, nickname: e.target.value })}
                       className="h-12 rounded-xl bg-white" data-testid="onb-nickname" placeholder="e.g. Ben" />
              </div>
              <SelectField label="Age range" value={child.age_range} testid="onb-age" onChange={(v) => setChild({ ...child, age_range: v })} options={C.AGE_RANGES} />
              <SelectField label="Where are you in the diagnosis journey?" value={child.diagnosis_status} testid="onb-diagnosis" onChange={(v) => setChild({ ...child, diagnosis_status: v })} options={C.DIAGNOSIS_STATUS} />
              <SelectField label="Assessment status" value={child.assessment_status} optional testid="onb-assessment" onChange={(v) => setChild({ ...child, assessment_status: v })} options={C.ASSESSMENT_STATUS} />
              <SelectField label="School status" value={child.school_status} optional testid="onb-school-status" onChange={(v) => setChild({ ...child, school_status: v })} options={C.SCHOOL_STATUS} />
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-extrabold text-kid-navy">Support & services</h2>
                <p className="text-kid-muted text-sm mt-1">Skip anything you're not ready to answer.</p>
              </div>
              <ChipMulti label="Main support needs" values={child.main_support_needs} optional options={C.SUPPORT_NEEDS} onToggle={(v) => toggle(child, setChild, "main_support_needs", v)} />
              <ChipMulti label="Current services" values={child.current_services} optional options={C.SERVICES} onToggle={(v) => toggle(child, setChild, "current_services", v)} />
              <SelectField label="Funding status" value={child.funding_status} optional testid="onb-funding-status" onChange={(v) => setChild({ ...child, funding_status: v })} options={C.FUNDING_STATUS} />
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-extrabold text-kid-navy">Funding awareness</h2>
                <p className="text-kid-muted text-sm mt-1">This helps us suggest what may be worth reviewing.</p>
              </div>
              <SelectField label="Disability Tax Credit (DTC) status" value={child.dtc_status} optional testid="onb-dtc" onChange={(v) => setChild({ ...child, dtc_status: v })} options={C.DTC_STATUS} />
              <SelectField label="Child Disability Benefit (CDB) status" value={child.cdb_status} optional testid="onb-cdb" onChange={(v) => setChild({ ...child, cdb_status: v })} options={C.CDB_STATUS} />
              <SelectField label="Ontario Autism Program (OAP) status" value={child.oap_status} optional testid="onb-oap" onChange={(v) => setChild({ ...child, oap_status: v })} options={C.OAP_STATUS} />
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-extrabold text-kid-navy">School support</h2>
                <p className="text-kid-muted text-sm mt-1">What, if anything, feels most pressing at school?</p>
              </div>
              <ChipMulti label="School support concerns" values={child.school_support_concerns} optional options={C.SCHOOL_CONCERNS} onToggle={(v) => toggle(child, setChild, "school_support_concerns", v)} />
            </div>
          )}

          {step === 5 && (
            <div className="space-y-5">
              <div>
                <h2 className="text-2xl font-extrabold text-kid-navy">Your main priority</h2>
                <p className="text-kid-muted text-sm mt-1">We'll put this at the top of your roadmap.</p>
              </div>
              <SelectField label="Right now, what matters most?" value={answers.urgent_priority} testid="onb-priority" onChange={(v) => setAnswers({ ...answers, urgent_priority: v })} options={C.URGENT_PRIORITY} />
              <ChipMulti label="Which documents do you already have?" values={answers.documents_available} optional
                         options={[{ value: "diagnosis_report", label: "Diagnosis report" }, { value: "assessment", label: "Assessment" }, { value: "iep", label: "IEP" }, { value: "therapy_reports", label: "Therapy reports" }, { value: "receipts", label: "Receipts" }, { value: "school_notes", label: "School notes" }]}
                         onToggle={(v) => toggle(answers, setAnswers, "documents_available", v)} />
            </div>
          )}

          <div className="flex items-center justify-between gap-3 mt-8 pt-6 border-t border-kid-line">
            {step > 0 ? (
              <button onClick={() => setStep((s) => s - 1)} className="kid-btn kid-btn-ghost h-11 px-5" data-testid="onb-back"><ArrowLeft size={16} /> Back</button>
            ) : <span />}
            <div className="flex items-center gap-3">
              <button onClick={saveExit} disabled={busy} className="text-sm text-kid-muted hover:text-kid-teal font-poppins" data-testid="onb-save-exit">Save & exit</button>
              {step < STEPS.length - 1 ? (
                <button onClick={next} disabled={busy} className="kid-btn kid-btn-primary h-11 px-6" data-testid="onb-next">
                  {busy ? "Saving…" : "Save & continue"} <ArrowRight size={16} />
                </button>
              ) : (
                <button onClick={finish} disabled={busy} className="kid-btn kid-btn-primary h-11 px-6" data-testid="onb-finish">
                  {busy ? "Generating roadmap…" : "Generate my roadmap"} <Check size={16} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
